import { GeneratedAnswerInterface } from "@amzn/ask-legal-domain";
import { API as RestAPI, Auth } from "aws-amplify";
import { AppContextValue } from "../setup/context-value";
import { APIResponse, toDefaultRequestPayload } from "./common";
import { fetchWithCloudFrontAuth } from "../utils/cloudfront-auth";

export interface GeneratedAnswerAPI {
    generateAnswer(
        request: GeneratedAnswerInterface.GeneratedAnswerRequest
    ): Promise<APIResponse<GeneratedAnswerInterface.GeneratedAnswerResponse>>;

    generateAnswerStream(
        request: GeneratedAnswerInterface.GeneratedAnswerRequest
    ): Promise<ReadableStream<GeneratedAnswerInterface.StreamingEvent>>;

    submitFeedback(
        request: GeneratedAnswerInterface.SubmitFeedbackRequest
    ): Promise<APIResponse<GeneratedAnswerInterface.SubmitFeedbackResponse>>;
}

export class GeneratedAnswerAPIImpl implements GeneratedAnswerAPI {
    constructor(private context: AppContextValue) {}

    async generateAnswer(
        request: GeneratedAnswerInterface.GeneratedAnswerRequest
    ): Promise<APIResponse<GeneratedAnswerInterface.GeneratedAnswerResponse>> {
        const response: APIResponse<GeneratedAnswerInterface.GeneratedAnswerResponse> = await RestAPI.post(
            "custom-domain",
            "/generated-answer/generate",
            toDefaultRequestPayload(request)
        );
        return response;
    }

    async generateAnswerStream(
        request: GeneratedAnswerInterface.GeneratedAnswerRequest
    ): Promise<ReadableStream<GeneratedAnswerInterface.StreamingEvent>> {
        const streamingEndpoint = "/api/generated-answer/streaming";

        try {
            const requestBody = JSON.stringify(toDefaultRequestPayload(request));
            const authToken = await this.getAuthToken();

            const response = await fetchWithCloudFrontAuth(streamingEndpoint, {
                method: "POST",
                headers: {
                    "Authorization": `Bearer ${authToken}`
                },
                body: requestBody
            });

            if (!response.body) {
                console.error("CloudFront returned empty body");
                throw new Error("No response body for streaming");
            }

            const transformStream = new TransformStream({
                transform(chunk, controller) {
                    const decoder = new TextDecoder();
                    const text = decoder.decode(chunk);

                    if (!text || text.trim() === "") {
                        console.warn("Received empty SSE text");
                        return;
                    }

                    // Parse Server-Sent Events format from Lambda
                    const lines = text.split("\n");
                    for (const line of lines) {
                        if (line.startsWith("data: ")) {
                            try {
                                const data = JSON.parse(line.substring(6));
                                controller.enqueue(data);
                            } catch (error) {
                                console.error("[UI-API] Error parsing SSE data:", error, "Raw line:", line);
                            }
                        }
                    }
                }
            });
            return response.body.pipeThrough(transformStream);
        } catch (error) {
            console.error("search streaming failed: ", error);
        }
    }

    private async getAuthToken(): Promise<string> {
        try {
            const session = await Auth.currentSession();
            const token = session.getIdToken().getJwtToken();
            return token;
        } catch (error) {
            console.error("Failed to get JWT token:", error);
        }
    }

    async submitFeedback(
        request: GeneratedAnswerInterface.SubmitFeedbackRequest
    ): Promise<APIResponse<GeneratedAnswerInterface.SubmitFeedbackResponse>> {
        const response: APIResponse<GeneratedAnswerInterface.SubmitFeedbackResponse> = await RestAPI.post(
            "custom-domain",
            "/generated-answer/submit-feedback",
            toDefaultRequestPayload(request)
        );
        return response;
    }
}